import { useCallback, useMemo, useRef, useState } from 'react';
import {
  getMyConversationEncryptionKey,
  getMissingEncryptionKeys,
  getUserEncryptionKeys,
  provideConversationEncryptionKey,
  registerEncryptionKey,
  requestEncryptionKeys,
  storeConversationEncryptionKey,
} from '../api/messaging.api';
import { isResponderE2EEEnabled } from '../config/e2eeFeatureFlag';
import {
  encryptConversationKeyForDevice,
  decryptConversationKeyFromBundle,
  decryptTextForConversation,
  encryptTextForConversation,
  generateConversationKeyHex,
  generateDeviceKeyPairPem,
} from '../utils/e2eeCrypto';
import {
  clearConversationKeys,
  getConversationKey,
  getDeviceIdentity,
  getOrCreateDeviceId,
  hasFailedDecryption,
  markFailedDecryption,
  removeConversationKey as removeConversationKeyFromStore,
  setConversationKey,
  setDeviceIdentity,
} from '../utils/e2eeStorage';
import {
  coerceConversationId,
  shouldTreatConversationAsEncrypted,
  toUserIdSet,
} from '../utils/e2eeUtils';
import { KEY_SYNC_REASONS } from '../utils/e2eeKeySyncReasons';

const ENCRYPTION_VERSION = 1;

export function useE2EE() {
  const isEnabled = isResponderE2EEEnabled();
  const ensureDevicePromiseRef = useRef(null);
  const conversationKeyCacheRef = useRef(new Map());
  const keySyncTimeoutRef = useRef(null);
  const remoteHydratedConversationIdsRef = useRef(new Set());
  const [missingKeyConversationIds, setMissingKeyConversationIds] = useState([]);
  const [keySyncStatusByConversation, setKeySyncStatusByConversation] = useState({});
  const [keySyncReasonByConversation, setKeySyncReasonByConversation] = useState({});
  const [keySyncError, setKeySyncError] = useState(null);

  const normalizeConversationIds = useCallback((conversations = []) => {
    const entries = Array.isArray(conversations) ? conversations : [];
    return Array.from(
      new Set(
        entries
          .map((conversation) => coerceConversationId(conversation))
          .filter((conversationId) => conversationId != null)
          .map((conversationId) => `${conversationId}`),
      ),
    );
  }, []);

  const mergeKeySyncStatuses = useCallback((conversationIds, status) => {
    const ids = Array.isArray(conversationIds) ? conversationIds : [];
    if (!ids.length) return;
    setKeySyncStatusByConversation((prev) => {
      const next = { ...prev };
      ids.forEach((conversationId) => {
        next[`${conversationId}`] = status;
      });
      return next;
    });
  }, []);

  const mergeKeySyncReasons = useCallback(
    (conversationIds, reason, { preserveExisting = false } = {}) => {
      const ids = Array.isArray(conversationIds) ? conversationIds : [];
      if (!ids.length || !reason) return;
      setKeySyncReasonByConversation((prev) => {
        const next = { ...prev };
        ids.forEach((conversationId) => {
          const key = `${conversationId}`;
          if (!preserveExisting || !next[key]) {
            next[key] = reason;
          }
        });
        return next;
      });
    },
    [],
  );

  const clearKeySyncStatuses = useCallback((conversationIds) => {
    const ids = Array.isArray(conversationIds) ? conversationIds : [];
    if (!ids.length) return;
    setKeySyncStatusByConversation((prev) => {
      let changed = false;
      const next = { ...prev };
      ids.forEach((conversationId) => {
        const key = `${conversationId}`;
        if (key in next) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const clearKeySyncState = useCallback(
    (conversationIds) => {
      const ids = Array.isArray(conversationIds) ? conversationIds : [];
      if (!ids.length) return;
      clearKeySyncStatuses(ids);
      setKeySyncReasonByConversation((prev) => {
        let changed = false;
        const next = { ...prev };
        ids.forEach((conversationId) => {
          const key = `${conversationId}`;
          if (key in next) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    },
    [clearKeySyncStatuses],
  );

  const markConversationKeyUnsynced = useCallback(
    (conversationId, reason, status = 'missing') => {
      if (conversationId == null) return;
      const conversationIdKey = `${conversationId}`;
      setMissingKeyConversationIds((prev) =>
        prev.some((id) => `${id}` === conversationIdKey) ? prev : [...prev, conversationIdKey],
      );
      mergeKeySyncStatuses([conversationId], status);
      mergeKeySyncReasons([conversationId], reason);
    },
    [mergeKeySyncReasons, mergeKeySyncStatuses],
  );

  const getCachedConversationKey = useCallback((conversationId) => {
    const cacheKey = `${conversationId}`;
    if (!conversationKeyCacheRef.current.has(cacheKey)) return null;
    return conversationKeyCacheRef.current.get(cacheKey) || null;
  }, []);

  const putCachedConversationKey = useCallback((conversationId, conversationKey) => {
    const cacheKey = `${conversationId}`;
    conversationKeyCacheRef.current.set(cacheKey, conversationKey || null);
  }, []);

  const ensureDeviceSetup = useCallback(async () => {
    if (!isEnabled) return null;
    if (ensureDevicePromiseRef.current) return ensureDevicePromiseRef.current;

    ensureDevicePromiseRef.current = (async () => {
      let identity = await getDeviceIdentity();
      if (!identity?.privateKeyPem || !identity?.publicKeyPem) {
        const deviceId = getOrCreateDeviceId();
        const keyPair = await generateDeviceKeyPairPem();
        identity = await setDeviceIdentity({
          deviceId,
          publicKeyPem: keyPair.publicKeyPem,
          privateKeyPem: keyPair.privateKeyPem,
          createdAt: new Date().toISOString(),
        });
      }

      await registerEncryptionKey({
        deviceId: identity.deviceId,
        publicKey: identity.publicKeyPem,
      });
      return identity;
    })().finally(() => {
      ensureDevicePromiseRef.current = null;
    });

    return ensureDevicePromiseRef.current;
  }, [isEnabled]);

  const getCurrentDeviceId = useCallback(async () => {
    if (!isEnabled) return null;
    const identity = await ensureDeviceSetup();
    return identity?.deviceId || null;
  }, [ensureDeviceSetup, isEnabled]);

  const hydrateConversationKey = useCallback(
    async (conversationId) => {
      const stored = await getConversationKey(conversationId);
      if (stored?.conversationKey) {
        putCachedConversationKey(conversationId, stored.conversationKey);
        return stored.conversationKey;
      }

      const identity = await ensureDeviceSetup();
      const result = await getMyConversationEncryptionKey(conversationId, identity.deviceId);
      const bundle = result?.bundle || result?.data?.bundle || result?.data || null;

      if (!bundle?.encryptedKey) {
        return null;
      }

      const decryptedConversationKey = await decryptConversationKeyFromBundle(
        bundle.encryptedKey,
        identity.privateKeyPem,
      );

      await setConversationKey(conversationId, decryptedConversationKey, bundle.keyVersion || 1);
      putCachedConversationKey(conversationId, decryptedConversationKey);
      return decryptedConversationKey;
    },
    [ensureDeviceSetup, putCachedConversationKey],
  );

  const checkMissingKeys = useCallback(async () => {
    if (!isEnabled) return [];
    const identity = await ensureDeviceSetup();
    const result = await getMissingEncryptionKeys(identity?.deviceId);
    const conversations = result?.conversations || result?.data?.conversations || [];
    const conversationIds = normalizeConversationIds(conversations);

    setMissingKeyConversationIds(conversationIds);
    setKeySyncStatusByConversation((prev) => {
      const missingIds = new Set(conversationIds);
      const next = {};
      Object.entries(prev).forEach(([conversationId, status]) => {
        if (missingIds.has(conversationId) || status === 'hydrating') {
          next[conversationId] = status;
        }
      });
      return next;
    });
    setKeySyncReasonByConversation((prev) => {
      const missingIds = new Set(conversationIds);
      const next = {};
      Object.entries(prev).forEach(([conversationId, reason]) => {
        if (missingIds.has(conversationId)) {
          next[conversationId] = reason;
        }
      });
      return next;
    });
    mergeKeySyncStatuses(conversationIds, 'missing');
    mergeKeySyncReasons(conversationIds, KEY_SYNC_REASONS.MISSING_LOCAL_KEY, {
      preserveExisting: true,
    });
    return conversations;
  }, [
    ensureDeviceSetup,
    isEnabled,
    mergeKeySyncReasons,
    mergeKeySyncStatuses,
    normalizeConversationIds,
  ]);

  const requestMissingKeys = useCallback(async () => {
    if (!isEnabled) return [];

    let pendingRequestIds = [];
    try {
      setKeySyncError(null);
      const identity = await ensureDeviceSetup();
      const conversations = await checkMissingKeys();
      const conversationIds = normalizeConversationIds(conversations);

      if (!conversationIds.length) {
        setMissingKeyConversationIds([]);
        setKeySyncReasonByConversation({});
        return [];
      }

      mergeKeySyncStatuses(conversationIds, 'requesting');
      mergeKeySyncReasons(conversationIds, KEY_SYNC_REASONS.REMOTE_DEVICE_NEEDED);
      pendingRequestIds = conversationIds;
      const result = await requestEncryptionKeys(identity.deviceId);
      const requestedConversations =
        result?.conversations || result?.data?.conversations || conversations;
      const requestedConversationIds = normalizeConversationIds(requestedConversations);
      const waitingIds = requestedConversationIds.length
        ? requestedConversationIds
        : conversationIds;

      setMissingKeyConversationIds(waitingIds);
      mergeKeySyncStatuses(waitingIds, 'waiting');
      mergeKeySyncReasons(waitingIds, KEY_SYNC_REASONS.REMOTE_DEVICE_NEEDED);

      if (keySyncTimeoutRef.current) clearTimeout(keySyncTimeoutRef.current);
      keySyncTimeoutRef.current = setTimeout(() => {
        keySyncTimeoutRef.current = null;
        const timedOutIds = [];
        setKeySyncStatusByConversation((prev) => {
          const next = { ...prev };
          let changed = false;
          Object.keys(next).forEach((id) => {
            if (next[id] === 'waiting' || next[id] === 'requesting') {
              next[id] = 'error';
              timedOutIds.push(id);
              changed = true;
            }
          });
          return changed ? next : prev;
        });
        mergeKeySyncReasons(timedOutIds, KEY_SYNC_REASONS.SYNC_TIMEOUT);
      }, 20000);

      return requestedConversations;
    } catch (error) {
      setKeySyncError(error?.message || 'Failed to request encryption keys');
      const failedIds = [];
      setKeySyncStatusByConversation((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((conversationId) => {
          if (next[conversationId] === 'requesting') {
            next[conversationId] = 'error';
            failedIds.push(conversationId);
          }
        });
        if (!failedIds.length) {
          pendingRequestIds.forEach((conversationId) => {
            next[`${conversationId}`] = 'error';
            failedIds.push(`${conversationId}`);
          });
        }
        return next;
      });
      mergeKeySyncReasons(failedIds, KEY_SYNC_REASONS.SYNC_REQUEST_FAILED);
      throw error;
    }
  }, [
    checkMissingKeys,
    ensureDeviceSetup,
    isEnabled,
    mergeKeySyncReasons,
    mergeKeySyncStatuses,
    normalizeConversationIds,
  ]);

  const getConversationKeyValue = useCallback(
    async (conversationId, { allowRemoteFetch = true } = {}) => {
      if (!isEnabled || conversationId == null) return null;
      const conversationIdKey = `${conversationId}`;

      const cached = getCachedConversationKey(conversationId);
      if (cached) return cached;

      const stored = await getConversationKey(conversationId);
      if (stored?.conversationKey) {
        putCachedConversationKey(conversationId, stored.conversationKey);
        return stored.conversationKey;
      }

      if (!allowRemoteFetch) return null;
      if (keySyncStatusByConversation?.[conversationIdKey] === 'error') {
        return null;
      }

      try {
        return await hydrateConversationKey(conversationId);
      } catch (error) {
        const statusCode = error?.response?.status;
        if (statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    [
      getCachedConversationKey,
      hydrateConversationKey,
      isEnabled,
      keySyncStatusByConversation,
      putCachedConversationKey,
    ],
  );

  const shareConversationKeyForUser = useCallback(
    async (conversationId, userId, conversationKey) => {
      const keysPayload = await getUserEncryptionKeys(userId);
      const keys = Array.isArray(keysPayload?.keys) ? keysPayload.keys : [];
      if (!keys.length) {
        return 0;
      }

      const postedBundles = await Promise.all(
        keys.map(async (keyEntry) => {
          const encryptedKey = await encryptConversationKeyForDevice(
            conversationKey,
            keyEntry.publicKey,
          );
          await storeConversationEncryptionKey(conversationId, {
            userId,
            deviceId: keyEntry.deviceId || null,
            encryptedKey,
            keyVersion: ENCRYPTION_VERSION,
          });
          return keyEntry.deviceId;
        }),
      );

      return postedBundles.length;
    },
    [],
  );

  const createAndShareConversationKey = useCallback(
    async (conversation) => {
      if (!isEnabled || !conversation) return null;
      const conversationId = coerceConversationId(conversation);
      if (conversationId == null) return null;

      await ensureDeviceSetup();

      const participantUserIds = toUserIdSet(conversation);
      if (!participantUserIds.length) {
        return null;
      }

      const conversationKey = generateConversationKeyHex();
      await setConversationKey(conversationId, conversationKey, ENCRYPTION_VERSION);
      putCachedConversationKey(conversationId, conversationKey);

      await Promise.all(
        participantUserIds.map((participantUserId) =>
          shareConversationKeyForUser(conversationId, participantUserId, conversationKey),
        ),
      );

      const conversationIdKey = `${conversationId}`;
      setMissingKeyConversationIds((prev) =>
        prev.some((id) => `${id}` === conversationIdKey)
          ? prev.filter((id) => `${id}` !== conversationIdKey)
          : prev,
      );
      clearKeySyncState([conversationId]);

      return conversationKey;
    },
    [
      clearKeySyncState,
      ensureDeviceSetup,
      isEnabled,
      putCachedConversationKey,
      shareConversationKeyForUser,
    ],
  );

  const shareConversationKeyWithUser = useCallback(
    async (conversationId, userId) => {
      if (!isEnabled || conversationId == null || userId == null) return 0;
      const conversationKey = await getConversationKeyValue(conversationId, {
        allowRemoteFetch: false,
      });
      if (!conversationKey) {
        return 0;
      }
      return shareConversationKeyForUser(conversationId, userId, conversationKey);
    },
    [getConversationKeyValue, isEnabled, shareConversationKeyForUser],
  );

  const provideKeysForReencryptionRequest = useCallback(
    async (event) => {
      if (!isEnabled || !event) return { provided: 0, skipped: 0, failed: 0 };

      const identity = await ensureDeviceSetup();
      const requestingDeviceId =
        event.requestingDeviceId ||
        event.requesting_device_id ||
        event.targetDeviceId ||
        event.target_device_id ||
        null;
      const requestingDevicePublicKey =
        event.requestingDevicePublicKey || event.requesting_device_public_key || null;
      const requestingUserId = event.userId || event.user_id || null;
      const conversationIds = Array.isArray(event.conversationIds)
        ? event.conversationIds
        : Array.isArray(event.conversation_ids)
          ? event.conversation_ids
          : [];

      if (
        !requestingDeviceId ||
        !requestingDevicePublicKey ||
        !requestingUserId ||
        !conversationIds.length
      ) {
        return { provided: 0, skipped: conversationIds.length, failed: 0 };
      }

      if (`${requestingDeviceId}` === `${identity.deviceId}`) {
        return { provided: 0, skipped: conversationIds.length, failed: 0 };
      }

      console.info('[[E2EE]] web <- mobile key sync request received', {
        requestingDeviceId: `${requestingDeviceId}`,
        conversationIds,
      });

      const results = await Promise.allSettled(
        conversationIds.map(async (conversationId) => {
          const conversationKey = await getConversationKeyValue(conversationId, {
            allowRemoteFetch: false,
          });
          if (!conversationKey) {
            return { status: 'skipped' };
          }

          const encryptedKey = await encryptConversationKeyForDevice(
            conversationKey,
            requestingDevicePublicKey,
          );

          await provideConversationEncryptionKey(conversationId, {
            targetUserId: requestingUserId,
            targetDeviceId: requestingDeviceId,
            encryptedKey,
            keyVersion: ENCRYPTION_VERSION,
            providingDeviceId: identity.deviceId,
          });

          console.info('[[E2EE]] web -> mobile conversation key sent', {
            conversationId,
            targetDeviceId: `${requestingDeviceId}`,
            providingDeviceId: `${identity.deviceId}`,
          });

          return { status: 'provided' };
        }),
      );

      return results.reduce(
        (summary, result) => {
          if (result.status === 'rejected') {
            console.error(
              '[MessagingE2EE] provideKeysForReencryptionRequest — conversation failed',
              result.reason,
            );
            summary.failed += 1;
            return summary;
          }
          if (result.value?.status === 'provided') {
            summary.provided += 1;
          } else {
            summary.skipped += 1;
          }
          return summary;
        },
        { provided: 0, skipped: 0, failed: 0 },
      );
    },
    [ensureDeviceSetup, getConversationKeyValue, isEnabled],
  );

  const hydrateAvailableKey = useCallback(
    async (conversationId) => {
      if (!isEnabled || conversationId == null) return null;

      mergeKeySyncStatuses([conversationId], 'hydrating');
      try {
        const conversationKey = await hydrateConversationKey(conversationId);
        if (conversationKey) {
          const conversationIdKey = `${conversationId}`;
          remoteHydratedConversationIdsRef.current.add(conversationIdKey);
          setMissingKeyConversationIds((prev) =>
            prev.filter(
              (missingConversationId) => `${missingConversationId}` !== conversationIdKey,
            ),
          );
          clearKeySyncState([conversationId]);
        } else {
          mergeKeySyncStatuses([conversationId], 'waiting');
          mergeKeySyncReasons([conversationId], KEY_SYNC_REASONS.REMOTE_DEVICE_NEEDED);
        }
        return conversationKey;
      } catch (error) {
        setKeySyncError(error?.message || 'Failed to hydrate encryption key');
        mergeKeySyncStatuses([conversationId], 'error');
        mergeKeySyncReasons([conversationId], KEY_SYNC_REASONS.REMOTE_DEVICE_BAD_KEY);
        throw error;
      }
    },
    [
      clearKeySyncState,
      hydrateConversationKey,
      isEnabled,
      mergeKeySyncReasons,
      mergeKeySyncStatuses,
    ],
  );

  const buildEncryptedSendPayload = useCallback(
    async (conversationId, textContent, metadata = null) => {
      const conversationKey = await getConversationKeyValue(conversationId);
      if (!conversationKey) {
        throw new Error('Conversation key not available');
      }

      const encryptedContent = await encryptTextForConversation(conversationKey, textContent);
      const metadataString =
        metadata && Object.keys(metadata).length ? JSON.stringify(metadata) : null;
      const encryptedMetadata = metadataString
        ? await encryptTextForConversation(conversationKey, metadataString)
        : null;

      return {
        encryptedContent,
        encryptedMetadata,
        encryptionVersion: ENCRYPTION_VERSION,
      };
    },
    [getConversationKeyValue],
  );

  const decryptMessage = useCallback(
    async (conversationId, message) => {
      const encryptedContent = message?.encryptedContent || message?.encrypted_content || null;
      if (!encryptedContent) return message;
      const messageId = message?.id ?? message?.message_id ?? 'unknown';
      const wasRecentlyHydratedFromRemote = remoteHydratedConversationIdsRef.current.has(
        `${conversationId}`,
      );
      const failedDecryptionReason = wasRecentlyHydratedFromRemote
        ? KEY_SYNC_REASONS.REMOTE_DEVICE_BAD_KEY
        : KEY_SYNC_REASONS.BAD_LOCAL_KEY;

      if (await hasFailedDecryption(conversationId, messageId)) {
        markConversationKeyUnsynced(conversationId, failedDecryptionReason);
        return {
          ...message,
          content: 'Unable to decrypt message',
          __e2ee: { status: 'failed' },
        };
      }

      const conversationKey = await getConversationKeyValue(conversationId);
      if (!conversationKey) {
        markConversationKeyUnsynced(conversationId, KEY_SYNC_REASONS.MISSING_LOCAL_KEY);
        return {
          ...message,
          content: 'Waiting for encryption key...',
          __e2ee: { status: 'pending-key' },
        };
      }

      try {
        const decryptedContent = await decryptTextForConversation(
          conversationKey,
          encryptedContent,
        );

        let decryptedMetadata = null;
        const encryptedMetadata = message?.encryptedMetadata || message?.encrypted_metadata;
        if (encryptedMetadata) {
          const metadataJson = await decryptTextForConversation(conversationKey, encryptedMetadata);
          decryptedMetadata = JSON.parse(metadataJson);
        }

        return {
          ...message,
          content: decryptedContent,
          metadata: decryptedMetadata
            ? { ...(message.metadata || {}), decrypted: decryptedMetadata }
            : message.metadata,
          __e2ee: { status: 'decrypted' },
        };
      } catch (error) {
        await markFailedDecryption(conversationId, messageId, error?.message || 'decrypt-failed');
        markConversationKeyUnsynced(conversationId, failedDecryptionReason);
        return {
          ...message,
          content: 'Unable to decrypt message',
          __e2ee: { status: 'failed' },
        };
      }
    },
    [getConversationKeyValue, markConversationKeyUnsynced],
  );

  const decryptMessagesForConversation = useCallback(
    async (conversationId, messages, conversation = null) => {
      if (!isEnabled) return messages;
      const messageList = Array.isArray(messages) ? messages : [];
      if (!shouldTreatConversationAsEncrypted(conversation, messageList)) {
        return messageList;
      }

      const decrypted = await Promise.all(
        messageList.map((message) => decryptMessage(conversationId, message)),
      );
      remoteHydratedConversationIdsRef.current.delete(`${conversationId}`);
      return decrypted;
    },
    [decryptMessage, isEnabled],
  );

  const removeConversationKey = useCallback(
    async (conversationId) => {
      if (!isEnabled || conversationId == null) return;
      conversationKeyCacheRef.current.delete(`${conversationId}`);
      await removeConversationKeyFromStore(conversationId);
    },
    [isEnabled],
  );

  const clearConversationKeyForTesting = useCallback(
    async (conversationId) => {
      if (!isEnabled || conversationId == null) return;
      const conversationIdKey = `${conversationId}`;
      conversationKeyCacheRef.current.delete(conversationIdKey);
      await removeConversationKeyFromStore(conversationId);
      setMissingKeyConversationIds((prev) =>
        prev.some((missingConversationId) => `${missingConversationId}` === conversationIdKey)
          ? prev
          : [...prev, conversationIdKey],
      );
      mergeKeySyncStatuses([conversationId], 'missing');
      mergeKeySyncReasons([conversationId], KEY_SYNC_REASONS.MISSING_LOCAL_KEY);
    },
    [isEnabled, mergeKeySyncReasons, mergeKeySyncStatuses],
  );

  const clearAllConversationKeys = useCallback(async () => {
    if (!isEnabled) return;
    conversationKeyCacheRef.current.clear();
    await clearConversationKeys();
  }, [isEnabled]);

  return useMemo(
    () => ({
      isEnabled,
      ensureDeviceSetup,
      getCurrentDeviceId,
      checkMissingKeys,
      requestMissingKeys,
      provideKeysForReencryptionRequest,
      hydrateAvailableKey,
      missingKeyConversationIds,
      keySyncStatusByConversation,
      keySyncReasonByConversation,
      keySyncError,
      createAndShareConversationKey,
      shareConversationKeyWithUser,
      getConversationKey: getConversationKeyValue,
      buildEncryptedSendPayload,
      decryptMessage,
      decryptMessagesForConversation,
      removeConversationKey,
      clearConversationKeyForTesting,
      clearAllConversationKeys,
    }),
    [
      isEnabled,
      ensureDeviceSetup,
      getCurrentDeviceId,
      checkMissingKeys,
      requestMissingKeys,
      provideKeysForReencryptionRequest,
      hydrateAvailableKey,
      missingKeyConversationIds,
      keySyncStatusByConversation,
      keySyncReasonByConversation,
      keySyncError,
      createAndShareConversationKey,
      shareConversationKeyWithUser,
      getConversationKeyValue,
      buildEncryptedSendPayload,
      decryptMessage,
      decryptMessagesForConversation,
      removeConversationKey,
      clearConversationKeyForTesting,
      clearAllConversationKeys,
    ],
  );
}
